import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import type { WithAuthenticatorProps } from '@aws-amplify/ui-react';
import { withAuthenticator } from '@aws-amplify/ui-react';
import { useAccountStatus } from '../../hooks/useAccountStatus';
import { useProfileData } from '../../hooks/useProfileData';
import { useCanManageAccount } from '../../hooks/usePermissions';
import { useTenantData } from '../../hooks/useTenantData';
import { getTierBadgeClass, getTierDisplayName } from '../../constants/subscriptionTiers';
import { RootState } from '../../Store/store';

function Avatar({ signOut, user }: WithAuthenticatorProps) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [theme, setTheme] = useState<'light' | 'dark' | 'auto'>(() => {
        const savedTheme = localStorage.getItem('theme-preference');
        return (savedTheme as 'light' | 'dark' | 'auto') || 'auto';
    });
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    const { hasUnlimitedUploads, hasUnlimitedAnalyzes, isLoading } = useAccountStatus();
    const authState = useSelector((state: RootState) => state.auth);
    const { profileData } = useProfileData();
    const { canManageAccount } = useCanManageAccount();
    const { tenantData } = useTenantData();
    
    // Use profile data from /profile/me endpoint, fallback to Cognito attributes
    const profileName = profileData?.first_name && profileData?.last_name 
        ? `${profileData.first_name} ${profileData.last_name}`
        : "";
    const name = profileName || user?.attributes?.name || user?.username || "";
    const email = user?.attributes?.email || "";
    const initials = name.split(" ").map((n) => n[0]).join("");

    // Apply theme on component mount and when theme changes
    useEffect(() => {
        applyTheme(theme);
    }, [theme]);

    // Listen for system theme changes when in auto mode
    useEffect(() => {
        if (theme === 'auto') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleChange = () => applyTheme('auto');
            
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }
    }, [theme]);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Apply theme function
    const applyTheme = (selectedTheme: 'light' | 'dark' | 'auto') => {
        const html = document.documentElement;
        
        if (selectedTheme === 'light') {
            html.classList.remove('dark');
            html.setAttribute('data-theme', 'light');
        } else if (selectedTheme === 'dark') {
            html.classList.add('dark');
            html.setAttribute('data-theme', 'dark');
        } else {
            // Auto mode - check system preference
            if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
                html.classList.add('dark');
                html.setAttribute('data-theme', 'dark');
            } else {
                html.classList.remove('dark');
                html.setAttribute('data-theme', 'light');
            }
        }
    };

    // Handle theme toggle
    const handleThemeToggle = () => {
        const themes: ('light' | 'dark' | 'auto')[] = ['light', 'dark', 'auto'];
        const currentIndex = themes.indexOf(theme);
        const nextTheme = themes[(currentIndex + 1) % themes.length];
        setTheme(nextTheme);
        localStorage.setItem('theme-preference', nextTheme);
        applyTheme(nextTheme);
    };

    // Handle logout
    const handleLogout = async () => {
        try {
            if (signOut) {
                await signOut();
            }
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    // Format usage data
    const formatUsage = () => {
        if (isLoading) {
            return { 
                uploads: 'Loading...', 
                analyzes: 'Loading...',
                role: 'Loading...'
            };
        }
        
        return {
            uploads: hasUnlimitedUploads ? 'Unlimited uploads' : 'Limited uploads',
            analyzes: hasUnlimitedAnalyzes ? 'Unlimited analyzes' : 'Limited analyzes',
            role: 'User'
        };
    };

    const usage = formatUsage();

    return (
        <div className="user-avatar-container mr-4 relative" ref={dropdownRef}>
            <div 
                className="user-avatar-tooltip cursor-pointer"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
                <div className="user-avatar avatar online placeholder">
                    <div className="user-avatar-circle bg-neutral-focus text-neutral-content rounded-full w-12">
                        <span className="user-avatar-initials">{initials}</span>
                    </div>
                </div>
            </div>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
                <div className="user-dropdown absolute right-0 top-14 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                    {/* User Info Section */}
                    <div className="user-dropdown-header p-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-3">
                            <div className="user-dropdown-avatar w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                <span className="text-blue-600 dark:text-blue-400 font-semibold text-lg">{initials}</span>
                            </div>
                            <div className="user-dropdown-info flex-1 min-w-0">
                                <div className="user-dropdown-name text-gray-900 dark:text-white font-semibold truncate">
                                    {name}
                                </div>
                                <div className="user-dropdown-email text-gray-500 dark:text-gray-400 text-sm truncate">
                                    {email}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Subscription Tier Section */}
                    <div className="user-dropdown-tier p-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="user-dropdown-tier-title text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Current Plan
                        </div>
                        <div className="user-dropdown-tier-badge">
                            {tenantData?.subscription ? (
                                <span className={`badge ${getTierBadgeClass(tenantData.subscription)}`}>
                                    {getTierDisplayName(tenantData.subscription)}
                                </span>
                            ) : (
                                <span className="badge badge-neutral">Free</span>
                            )}
                        </div>
                    </div>

                    {/* Actions Section */}
                    <div className="user-dropdown-actions p-2">
                        
                        <Link 
                            to="/profile" 
                            className="user-dropdown-action flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                        >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Edit Profile
                        </Link>
                        
                        {canManageAccount && (
                            <Link 
                                to="/billing" 
                                className="user-dropdown-action flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                                onClick={() => setIsDropdownOpen(false)}
                            >
                                <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                                Billing & Account
                            </Link>
                        )}
                        
                        <button 
                            className="user-dropdown-action flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                            onClick={handleThemeToggle}
                        >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                            Theme: {theme.charAt(0).toUpperCase() + theme.slice(1)}
                        </button>
                        
                        <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                        
                        <button 
                            className="user-dropdown-action flex items-center w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                            onClick={handleLogout}
                        >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Log out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default withAuthenticator(Avatar);